import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CloseIcon,
  Text,
  TextField,
  makeStyles,
} from '@knockrentals/knock-shared-web';
import { ErrorAlert } from '../ErrorAlert';

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },

  dialogContent: {
    height: '300px',
  },

  dialogActions: {
    padding: '16px',
  },

  endAdornment: {
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiSvgIcon-fontSizeSmall': {
        fontSize: '20px',
      },

      '& .MuiAutocomplete-popupIndicator': {
        '& .MuiSvgIcon-root': {
          fontSize: '24px',
        },
      },
    },
  },
}));

export const ManageCompanySourceView = ({
  buttonText,
  errorMessage,
  getOptionsDisabled,
  onClose,
  onSubmit,
  setErrorMessage,
  sources,
}) => {
  const classes = useStyles();
  const [currentSource, setCurrentSource] = useState(null);
  const [hasError, setHasError] = useState(false);
  const handleSubmit = () => {
    if (currentSource) {
      onSubmit(currentSource);
    } else {
      setHasError(true);
    }
  };

  const handleSourceChange = (_, value) => {
    setCurrentSource(value);

    if (value) {
      setHasError(false);
    }
  };

  const handleOnFocus = () => {
    if (errorMessage) {
      setErrorMessage();
    }
  };

  const hasSources = sources && sources.length > 0;

  return (
    <React.Fragment>
      <DialogTitle disableTypography>
        <Text variant="h6">{buttonText}</Text>

        <IconButton
          aria-label="close the popup"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        <Box>
          <ErrorAlert alertMessage={errorMessage} isVisible={errorMessage} />

          {hasSources && (
            <Autocomplete
              getOptionDisabled={(option) => getOptionsDisabled(option)}
              getOptionLabel={(option) => option.label}
              onChange={handleSourceChange}
              onFocus={handleOnFocus}
              options={sources || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.endAdornment}
                  error={hasError}
                  helperText={hasError ? 'Please select a company source' : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Source"
                />
              )}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button disabled={!hasSources} onClick={handleSubmit}>
          {buttonText}
        </Button>
      </DialogActions>
    </React.Fragment>
  );
};

ManageCompanySourceView.propTypes = {
  buttonText: PropTypes.string,
  errorMessage: PropTypes.string,
  getOptionsDisabled: PropTypes.func,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  setErrorMessage: PropTypes.func,
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      companyMarketingSourceId: PropTypes.number,
      leasingTeamEnabledCount: PropTypes.number,
      id: PropTypes.number,
      isDefault: PropTypes.bool,
      isEnabled: PropTypes.bool,
      name: PropTypes.string,
    })
  ),
};
